<template lang="html">
  <!--	TabBar-->
  <div class="benefits">
    <NavBar
      titleTranslationPath="benefitsOverview.title"
      location="location"
      favorites="favorites"
      preferences="preferences"
      commune="commune"
    />
    <Banner :useTriggerRequestReview="true" />
    <div class="communes-selector" v-if="scopeOptions.length > 1">
      <SelectField
        identifier="scope-selector"
        label="locationOptions.commune.select"
        v-model="scopeSelected"
        :options="scopeOptions"
        :is-mandatory="false"
      >
      </SelectField>
    </div>
    <div class="content">
      <div class="search" v-if="pagedata && pagedata.isSearching">
        <div class="searchbox">
          <font-awesome-icon class="searchbox-icon" icon="search" />
          <input type="text" placeholder="" />
        </div>
        <Button class="searchbutton" center="center" :path="benefitsOverview.button.search"></Button>
      </div>
      <NewTranslation
        class="headline"
        path="benefitsOverview.headline"
        :params="{ commune: currentLocation && currentLocation.title }"
      ></NewTranslation>
      <MapOrListButton
        v-if="getBenefitsWithValidPos.length > 0"
        ref="benefitsMapBtn"
        :disabled="isListLoading"
        v-on:click="changeView"
        class="btn-map-list"
        :isList="viewSelected === 'LIST' ? true : false"
        center="center"
      ></MapOrListButton>
      <div class="benefit-list" v-if="viewSelected === 'LIST'">
        <router-link
          :to="{
            name: $namedRoutes.benefitsCategory,
            query: {
              categoryName: category.name,
              scopeSelected: scopeSelected,
            },
          }"
          class="benefit-entry"
          v-for="(category, index) in getBenefitCategoriesByName"
          :key="index"
        >
          <img class="benefit-image" :src="getCategoryImageUrl(category)" />
          <div class="benefit-info">
            <div class="benefit-amount">{{ category.count }}</div>
            <div class="benefit-name">{{ category.name }}</div>
          </div>
        </router-link>
      </div>
      <div class="benefit__map" ref="benefitsMap" v-else>
        <div class="benefit__map-container" v-if="getBenefitsWithValidPos.length > 0">
          <l-map
            class="benefit__leaflet"
            :style="mapRealHeight"
            :zoom="mapZoom"
            :center="getMapCenter()"
            ref="map"
            @ready="onMapReady"
          >
            <l-tile-layer :url="mapUrl" :attribution="mapAttribution"></l-tile-layer>
            <l-marker
              v-for="(benefit, index) in getBenefitsWithValidPos"
              :key="index"
              :lat-lng="[benefit.benefitLat, benefit.benefitLng]"
            >
              <l-popup class="map__popup">
                <div class="map__popup-title">{{ benefit.venueTitle }}</div>
                <img class="map__popup-img" :src="getBenefitImageUrl(benefit)" :alt="benefit.venueTitle" />
                <div v-html="getSafeBenefitText(benefit.benefitOffer)"></div>
                <div class="map__popup-links">
                  <a :href="assembleGoogleMapsURL(benefit)" target="_blank">
                    <font-awesome-icon icon="map-marked" class="icon" />
                  </a>
                  <router-link
                    :to="{
                      name: $namedRoutes.benefitsDetail,
                      query: {
                        benefitId: benefit.id,
                      },
                    }"
                  >
                    <font-awesome-icon icon="info-circle" class="icon" />
                  </router-link>
                </div>
              </l-popup>
            </l-marker>
          </l-map>
        </div>
      </div>
    </div>
    <TabBar />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import NavBar from '@/components/NavBar.vue';
import TabBar from '@/components/TabBar.vue';
import Banner from '@/components/Banner.vue';
import Button from '@/components/Button.vue';
import NewTranslation from '@/components/i18n/NewTranslation';
import RemoteAssets from '@/app/utils/RemoteAssets';
import SelectField from '../form/SelectField.vue';
import i18next from 'i18next';
import MapOrListButton from '@/components/MapOrListButton.vue';
import { LMap, LTileLayer, LMarker, LPopup } from 'vue2-leaflet';
import L from 'leaflet';

export default {
  components: {
    NewTranslation,
    NavBar,
    TabBar,
    Banner,
    Button,
    MapOrListButton,
    SelectField,
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
  },
  mounted() {},
  data() {
    return {
      scopeSelected: 'init',
      viewSelected: 'LIST',
      mapUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      mapAttribution: '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      mapZoom: 15,
      isListLoading: true,
      mapRealHeight: 'height: 100px',
    };
  },
  props: ['pagedata'],
  methods: {
    getCategoryImageUrl(category) {
      return RemoteAssets.benefitCategoryImageUrl(category.image);
    },
    normalizeLatLng(benefit) {
      if (!benefit) return null;

      const rawLat = benefit.benefitLat;
      const rawLng = benefit.benefitLng;

      const normalizeNumber = value => {
        if (value === null || value === undefined) return null;
        if (typeof value === 'number') return Number.isFinite(value) ? value : null;
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (!trimmed) return null;
          const normalized = trimmed.replace(',', '.');
          const parsed = Number.parseFloat(normalized);
          return Number.isFinite(parsed) ? parsed : null;
        }
        return null;
      };

      const lat = normalizeNumber(rawLat);
      const lng = normalizeNumber(rawLng);

      if (lat === null || lng === null) return null;
      if (lat < -90 || lat > 90) return null;
      if (lng < -180 || lng > 180) return null;

      return [lat, lng];
    },
    changeView() {
      this.viewSelected = this.viewSelected === 'LIST' ? 'MAP' : 'LIST';
      const benefitsMapBtn = this.$refs.benefitsMapBtn.$el;
      const buttonPosY = benefitsMapBtn.getBoundingClientRect().top;
      const buttonHeight = benefitsMapBtn.getBoundingClientRect().height;
      const viewPortHeight = window.innerHeight;
      const mapRealHeight = viewPortHeight - (buttonPosY + buttonHeight) - 80 - 40; //80 for bottom tab bars 40 for margin top and margin bottom
      this.mapRealHeight = `height: ${mapRealHeight}px`;

      if (this.viewSelected === 'MAP') {
        this.fitMarkers();
      }
    },
    onMapReady() {
      if (this.viewSelected === 'MAP') {
        this.fitMarkers();
      }
    },
    assembleGoogleMapsURL(benefit) {
      const benefitLat = benefit.benefitLat;
      const benefitLng = benefit.benefitLng;
      if (!!benefit.venueGoogleId) {
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          benefit.venueTitle
        )}&query_place_id=${benefit.venueGoogleId}`;
      }
      return `https://www.google.com/maps/search/?api=1&query=${benefitLat}%2C${benefitLng}`;
      //return `https://www.google.com/maps/search/?api=1&query=${benefitLat}%2C${benefitLng}+${encodeURIComponent(benefit.venueTitle)}`//With name
    },
    getBenefitImageUrl(benefit) {
      //return `https://backend.welcmpass.info/img/advantages/${benefit.venueImage}`;
      if (benefit.venueImage) {
        return RemoteAssets.benefitImageUrl(benefit.venueImage);
      } else if (benefit.externalImg) {
        return benefit.externalImg;
      }
      return '';
    },
    getMapCenter() {
      const firstBenefit = this.getBenefitsWithValidPos[0];
      const maybeLatLng = this.normalizeLatLng(firstBenefit);
      if (maybeLatLng) return maybeLatLng;
      return [52.52, 13.405]; // Default, Berlin
      //return [52.5200,13.4050]; //Default, Berlin
    },
    isBenefitPosValid(benefit) {
      return !!this.normalizeLatLng(benefit);
    },
    getSafeBenefitText(value) {
      if (value) {
        const resultParsed = `<div class="map__popup-offer">${value}</div>`;
        return resultParsed;
      }
      return '';
    },
    fitMarkers() {
      const benefits = this.getBenefitsWithValidPos;

      if (!benefits || benefits.length === 0) return;

      this.$nextTick(() => {
        const map = this.$refs.map && this.$refs.map.mapObject;
        if (!map) return;

        // Important when the map was just shown after v-if/v-else
        map.invalidateSize();

        if (benefits.length === 1) {
          const benefit = benefits[0];

          map.setView([benefit.benefitLat, benefit.benefitLng], 15);

          return;
        }

        const bounds = L.latLngBounds(benefits.map(benefit => [benefit.benefitLat, benefit.benefitLng]));

        map.fitBounds(bounds, {
          padding: [30, 30],
          maxZoom: 16,
        });
      });
    },
  },
  computed: {
    ...mapGetters(['currentLocation', 'benefits', 'benefitCategories', 'communeInfo']),
    getBenefitCategoriesByName() {
      const categoriesByName = {};
      for (let ind1 = 0; ind1 < this.benefitCategories.length; ind1++) {
        const benefitCategory = this.benefitCategories[ind1];
        if (benefitCategory.benefitCommuneId == this.scopeSelected || this.scopeSelected == 'all') {
          if (!categoriesByName.hasOwnProperty(benefitCategory.name)) {
            categoriesByName[benefitCategory.name] = {
              name: benefitCategory.name,
              image: benefitCategory.image,
              count: benefitCategory.count,
            };
          } else {
            categoriesByName[benefitCategory.name].count += benefitCategory.count;
          }
        }
      }
      return categoriesByName;
    },
    scopeOptions() {
      const categoryCommunes = {};
      for (let ind2 = 0; ind2 < this.benefitCategories.length; ind2++) {
        const benefitCategory = this.benefitCategories[ind2];
        categoryCommunes[benefitCategory.benefitCommuneId] = benefitCategory.benefitCommune;
      }
      const communeKeys = Object.keys(categoryCommunes);

      let resultCommunes = communeKeys.map(communeKey => {
        return { value: communeKey, label: categoryCommunes[communeKey] };
      });
      resultCommunes = [{ value: 'all', label: i18next.t('locationOptions.option.all') }, ...resultCommunes];
      if (this.scopeSelected == 'init' && this.currentLocation.id > 0) {
        // const optionFound = resultCommunes.some((element) => element.value == this.currentLocation.id);
        // if(optionFound) {
        // 	this.scopeSelected = this.currentLocation.id;
        // } else {
        this.scopeSelected = 'all';
        // }
        if (this.isListLoading) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.isListLoading = false;
            }, 2000);
          });
        }
      }

      return resultCommunes;
    },
    getBenefitsWithValidPos() {
      const benefitsWithValidPos = [];
      if (this.communeInfo.useAdvMap) {
        for (const benefit of this.benefits) {
          const normalized = this.normalizeLatLng(benefit);
          if (normalized) {
            const [benefitLat, benefitLng] = normalized;
            benefitsWithValidPos.push({
              ...benefit,
              benefitLat,
              benefitLng,
            });
          }
        }
      }
      return benefitsWithValidPos;
    },
  },
  watch: {
    getBenefitsWithValidPos() {
      if (this.viewSelected === 'MAP') {
        this.fitMarkers();
      }
    },
  },
};
</script>

<style lang="sass" scoped>
.benefits
	padding-bottom: 100px
	.communes-selector
		margin-top: 10px
.content
	padding: 20px

.search
	padding-bottom: 20px
	margin-bottom: 20px
	border-bottom: 1px solid #eee
	.searchbox
		position: relative
		border-radius: 10px
		background: #fff
		&-icon
			position: absolute
			width: 20px
			height: 20px
			color: #454F63
			margin: 15px 20px
			top: 3px
		input
			border-radius: 10px
			height: 100%
			padding: 15px 15px 15px 55px
			font-size: 1.25rem
			border: 1px solid #ccc
	.searchbutton
		margin-top: 20px
.btn-map-list
	margin-top: 15px
.headline
	font-size: 1.9rem
.benefit
	// &-list
		// margin-top: 20px
	&__map
		margin-top: 20px
	&-entry
		display: block
		height: 125px
		position: relative
		background-size: cover
		background-position: center center
		margin-top: 20px
		&[data-category="restaurant"]
			background-image: url(~@/assets/images/benefit-restaurants.jpeg)
		&[data-category="event"]
			background-image: url(~@/assets/images/benefit-konzerte.jpg)
		&[data-category="theater"]
			background-image: url(~@/assets/images/benefit-theater.jpg)
		&[data-category="kids"]
			background-image: url(~@/assets/images/benefit-kids.jpeg)
		&[data-category="sport"]
			background-image: url(~@/assets/images/benefit-sport.jpg)
	&-image
		width: 100%
		height: 100%
		object-fit: cover
		object-position: center center
		background-color: #ccc
	&-info
		position: absolute
		left: 0
		bottom: 0
		opacity: .9
		display: flex
		color: #fff
	&-amount
		display: flex
		width: 40px
		height: 40px
		justify-content: center
		align-items: center
		background-color: #DE3D36
	&-name
		min-width: 120px
		padding: 0 10px
		display: flex
		align-items: center
		background-color: #1DB2D1
.map__popup
	min-width: 130px
	&-img
		max-width: 100%
	&-title
		font-weight: bold
		text-align: center
		margin-bottom: 5px
	&-offer
		margin: 5px 0
	&-links
		display: flex
		justify-content: space-between
		& a
			display: block
		& svg
			width: 40px
			height: 40px
</style>
<style lang="scss">
.leaflet-popup-content .map__popup-offer {
  & > p {
    margin-top: 5px;
    margin-bottom: 5px;
  }
}
</style>
